// Shared utilities and small presentational components for the /vtas Dashboard.
// Extracted from Dashboard.jsx to keep the main page focused on data
// orchestration.
import React from 'react';
import { X } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

export const COLORS = ['#F4A261', '#457B9D', '#1B6B3A', '#FF6EC7', '#00F0FF', '#E63946', '#2A9D8F', '#9B59B6', '#3498DB', '#E67E22'];

export const formatMoney = (val, currency = 'ARS') => {
  if (val == null || isNaN(val)) return currency === 'USD' ? 'U$D 0' : '$0';
  const symbol = currency === 'USD' ? 'U$D ' : '$';
  const abs = Math.abs(val);
  if (abs >= 1000000) return `${symbol}${(val / 1000000).toFixed(2)}M`;
  if (abs >= 1000) return `${symbol}${(val / 1000).toFixed(1)}K`;
  return `${symbol}${val.toFixed(0)}`;
};

export const formatMonthLabel = (m) => {
  const parts = m.split('-');
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return months[parseInt(parts[1]) - 1] + ' ' + parts[0].slice(2);
};

export const CustomTooltip = ({ active, payload, label, moneyFormat = true, currency = 'ARS' }) => {
  if (!active || !payload || payload.length === 0) return null;
  const sorted = [...payload]
    .filter((p) => typeof p.value === 'number' && p.value !== 0)
    .sort((a, b) => b.value - a.value);
  if (sorted.length === 0) return null;
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-xl max-h-[420px] overflow-y-auto">
      <p className="text-white font-semibold text-sm mb-1">{label}</p>
      {sorted.map((p, i) => (
        <p key={i} className="text-sm" style={{ color: p.color }}>
          {p.name}: {typeof p.value === 'number' ? (moneyFormat ? formatMoney(p.value, currency) : p.value.toLocaleString('es-AR')) : p.value}
        </p>
      ))}
    </div>
  );
};

export const CountTooltip = (props) => <CustomTooltip {...props} moneyFormat={false} />;
export const MoneyTooltip = (currency) => (props) => <CustomTooltip {...props} currency={currency} />;

export const SectionTitle = ({ title, accent }) => (
  <div className="border-l-4 pl-4 py-2" style={{ borderColor: accent }}>
    <h2 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif", color: accent }}>
      {title}
    </h2>
  </div>
);

export const FilterChip = ({ color, label, onClear }) => {
  const palette = {
    orange: 'bg-orange-900/40 border-orange-700/50 text-orange-200',
    pink: 'bg-pink-900/40 border-pink-700/50 text-pink-200',
    blue: 'bg-blue-900/40 border-blue-700/50 text-blue-200',
    fuchsia: 'bg-fuchsia-900/40 border-fuchsia-700/50 text-fuchsia-200',
    green: 'bg-green-900/40 border-green-700/50 text-green-200',
  };
  return (
    <span className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded-full border ${palette[color] || palette.orange}`}>
      {label}
      <button onClick={onClear} className="hover:text-white"><X size={14} /></button>
    </span>
  );
};

export const KpiCard = ({ label, value, icon: Icon, color }) => (
  <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
    <CardContent className="p-5">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
          <Icon size={24} style={{ color }} />
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const RankingTable = ({ title, rows, nameKey, nameLabel, onRowClick, icon: Icon, accent, currency = 'ARS' }) => (
  <Card className="border-gray-800" style={{ backgroundColor: '#111' }}>
    <CardContent className="p-5">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
        {Icon && <Icon size={20} style={{ color: accent }} />} {title}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left text-gray-400 py-2 px-3">#</th>
              <th className="text-left text-gray-400 py-2 px-3">{nameLabel}</th>
              <th className="text-right text-gray-400 py-2 px-3">Campañas</th>
              <th className="text-right text-gray-400 py-2 px-3">Facturación</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} onClick={() => onRowClick && onRowClick(r)} className="border-b border-gray-800/50 hover:bg-white/10 cursor-pointer transition-colors">
                <td className="py-2 px-3 text-gray-500">{i + 1}</td>
                <td className="py-2 px-3 text-white font-medium">{r[nameKey]}</td>
                <td className="py-2 px-3 text-right text-gray-300">{r.campanas}</td>
                <td className="py-2 px-3 text-right font-semibold" style={{ color: accent || '#F4A261' }}>{formatMoney(r.valor, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </CardContent>
  </Card>
);

// ===== Last-12-months trailing window from multi-year data =====
export const buildLast12Months = (yearsMap, unit) => {
  // yearsMap: { '2025': {ar, intl}, '2026': {ar, intl} }
  // unit: 'ar' | 'intl'
  // Returns: [{ month: 'YYYY-MM', label: 'Mar 25', valor, campanas, ticket }]
  const flat = {};
  Object.values(yearsMap || {}).forEach((y) => {
    const src = y?.[unit];
    if (!src) return;
    (src.monthly || []).forEach((m) => {
      flat[m.month] = { valor: m.valor || 0, campanas: m.campanas || 0 };
    });
  });

  // Build last 12 months ending in the CURRENT month (inclusive)
  const now = new Date();
  const endY = now.getFullYear();
  const endM = now.getMonth() + 1;
  const out = [];
  for (let i = 11; i >= 0; i--) {
    let m = endM - i;
    let y = endY;
    while (m < 1) { m += 12; y -= 1; }
    const key = `${y}-${String(m).padStart(2, '0')}`;
    const d = flat[key] || { valor: 0, campanas: 0 };
    out.push({
      month: key,
      label: formatMonthLabel(key),
      valor: d.valor,
      campanas: d.campanas,
      ticket: d.campanas > 0 ? d.valor / d.campanas : 0,
    });
  }
  return out;
};
